import { createFileRoute } from '@tanstack/react-router'
import { Suspense } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { RefreshCw, BarChart3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { DateRangeSelector } from '@/components/analytics/DateRangeSelector'
import { MetricsOverview } from '@/components/analytics/MetricsOverview'
import { PerformanceChart } from '@/components/analytics/PerformanceChart'
import { TokenUsageChart } from '@/components/analytics/TokenUsageChart'
import { ModelDistribution } from '@/components/analytics/ModelDistribution'
import { CallMetricsTable } from '@/components/analytics/CallMetricsTable'
import { ExportButton } from '@/components/analytics/ExportButton'
import { useAnalyticsStore } from '@/stores/analytics.store'

/**
 * Error fallback component for analytics sections
 */
const ErrorFallback: React.FC<{ 
  error: any
  resetErrorBoundary: () => void 
  section?: string 
}> = ({ error, resetErrorBoundary, section = 'section' }) => {
  // Check for specific error types
  const isAccessDenied = 
    error?.response?.status === 403 || 
    error?.code === 'ERR_BAD_REQUEST' ||
    error?.message?.toLowerCase().includes('access denied')
  
  const isNotFound = error?.response?.status === 404
  const isServerError = error?.response?.status >= 500
  const isNetworkError = error?.code === 'ERR_NETWORK'
  
  const getErrorDetails = () => {
    if (isAccessDenied) {
      return {
        title: "Access Denied",
        description: "You don't have permission to view analytics data.",
        details: "Contact your administrator to request the 'analytics:view' permission for your account.",
        icon: "🔒",
        variant: "default" as const,
        showRetry: false
      }
    }
    
    if (isNotFound) {
      return {
        title: "Analytics Not Found",
        description: "The analytics service could not be found.",
        details: "Please ensure the analytics service is running and properly configured.",
        icon: "🔍",
        variant: "destructive" as const,
        showRetry: true
      }
    }
    
    if (isServerError) {
      return {
        title: "Server Error",
        description: "The analytics service encountered an error.",
        details: "Please try again later or contact support if the problem persists.",
        icon: "⚠️",
        variant: "destructive" as const,
        showRetry: true
      }
    }
    
    if (isNetworkError) {
      return {
        title: "Network Error",
        description: "Unable to connect to the analytics service.",
        details: "Please check your internet connection and try again.",
        icon: "🌐",
        variant: "destructive" as const,
        showRetry: true
      }
    }
    
    return {
      title: `Failed to load ${section}`,
      description: error?.message || "An unexpected error occurred.",
      details: "Please try refreshing the page or contact support.",
      icon: "❌",
      variant: "destructive" as const,
      showRetry: true
    }
  }
  
  const errorDetails = getErrorDetails()
  
  return (
    <Card className={errorDetails.variant === "default" ? "border-orange-200 dark:border-orange-800" : ""}>
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <span className="text-2xl">{errorDetails.icon}</span>
          <div className="flex-1 space-y-3">
            <div>
              <h3 className="font-semibold text-lg">{errorDetails.title}</h3>
              <p className="text-muted-foreground mt-1">
                {errorDetails.description}
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              {errorDetails.details}
            </p>
            {errorDetails.showRetry && (
              <Button onClick={resetErrorBoundary} size="sm" variant="outline" className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Try Again
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Loading fallback for chart sections
 */
const ChartLoadingFallback: React.FC<{ title: string }> = () => (
  <Card>
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32" />
      </div>
      <Skeleton className="h-[260px] w-full sm:h-[400px]" />
    </div>
  </Card>
)

/**
 * Analytics Dashboard Page
 */
function AnalyticsPageContent() {
  const { isLoading, lastRefreshTime, resetToDefaults } = useAnalyticsStore()

  const handleRefresh = () => {
    // Force refresh by resetting and re-triggering queries
    window.location.reload()
  }

  const handleReset = () => {
    resetToDefaults()
  }

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-6 w-6" />
            <h1 className="text-2xl font-bold tracking-tight">Analytics Dashboard</h1>
          </div>
          <p className="text-muted-foreground">
            Monitor call performance, token usage, and system metrics
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {lastRefreshTime && (
            <span className="hidden sm:inline text-sm text-muted-foreground">
              Last updated: {lastRefreshTime.toLocaleTimeString()}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <ExportButton />
        </div>
      </div>

      {/* Date Range and Controls */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <DateRangeSelector />
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="text-muted-foreground hover:text-foreground"
              >
                Reset to defaults
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Metrics Overview */}
      <section>
        <ErrorBoundary
          FallbackComponent={(props) => <ErrorFallback {...props} section="metrics overview" />}
          resetKeys={[lastRefreshTime]}
        >
          <Suspense fallback={<ChartLoadingFallback title="Metrics Overview" />}>
            <MetricsOverview />
          </Suspense>
        </ErrorBoundary>
      </section>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Performance Chart */}
        <ErrorBoundary
          FallbackComponent={(props) => <ErrorFallback {...props} section="performance chart" />}
          resetKeys={[lastRefreshTime]}
        >
          <Suspense fallback={<ChartLoadingFallback title="Performance Over Time" />}>
            <PerformanceChart />
          </Suspense>
        </ErrorBoundary>

        {/* Token Usage Chart */}
        <ErrorBoundary
          FallbackComponent={(props) => <ErrorFallback {...props} section="token usage chart" />}
          resetKeys={[lastRefreshTime]}
        >
          <Suspense fallback={<ChartLoadingFallback title="Token Usage" />}>
            <TokenUsageChart />
          </Suspense>
        </ErrorBoundary>
      </div>

      {/* Model Distribution */}
      <section>
        <ErrorBoundary
          FallbackComponent={(props) => <ErrorFallback {...props} section="model distribution" />}
          resetKeys={[lastRefreshTime]}
        >
          <Suspense fallback={<ChartLoadingFallback title="Model Distribution" />}>
            <ModelDistribution />
          </Suspense>
        </ErrorBoundary>
      </section>

      {/* Call Metrics Table */}
      <section>
        <ErrorBoundary
          FallbackComponent={(props) => <ErrorFallback {...props} section="call metrics table" />}
          resetKeys={[lastRefreshTime]}
        >
          <Suspense fallback={<ChartLoadingFallback title="Call Metrics" />}>
            <CallMetricsTable />
          </Suspense>
        </ErrorBoundary>
      </section>

      {/* Footer */}
      <div className="text-center text-sm text-muted-foreground py-6">
        <p>
          Analytics data is refreshed automatically. Export data for external analysis or reporting.
        </p>
      </div>
    </div>
  )
}

/**
 * Analytics Page with Query Error Reset Boundary
 * This wrapper ensures that query errors (like 403) are properly handled
 * and can be reset when permissions are fixed
 */
function AnalyticsPage() {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          FallbackComponent={(props) => (
            <ErrorFallback 
              {...props} 
              section="analytics dashboard"
            />
          )}
          onReset={reset}
        >
          <AnalyticsPageContent />
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  )
}

export const Route = createFileRoute('/analytics')({
  component: AnalyticsPage,
  
  // Add loading component for the entire route
  pendingComponent: () => (
    <div className="container mx-auto p-6">
      <div className="space-y-8">
        {/* Header skeleton */}
        <div className="flex justify-between items-start">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="flex gap-3">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-24" />
          </div>
        </div>
        
        {/* Controls skeleton */}
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-between items-center">
              <Skeleton className="h-9 w-80" />
              <Skeleton className="h-9 w-32" />
            </div>
          </CardContent>
        </Card>
        
        {/* Content skeletons */}
        <ChartLoadingFallback title="Metrics Overview" />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <ChartLoadingFallback title="Performance Chart" />
          <ChartLoadingFallback title="Token Usage Chart" />
        </div>
        <ChartLoadingFallback title="Model Distribution" />
        <ChartLoadingFallback title="Call Metrics Table" />
      </div>
    </div>
  ),

  // Add error component for route-level errors
  errorComponent: ({ error, reset }) => {
    // Check for specific error types
    const errorObj = error as any
    const isAccessDenied = 
      errorObj?.response?.status === 403 || 
      errorObj?.code === 'ERR_BAD_REQUEST' ||
      errorObj?.message?.toLowerCase().includes('access denied')
    
    const isNotFound = errorObj?.response?.status === 404
    const isServerError = errorObj?.response?.status >= 500
    const isNetworkError = errorObj?.code === 'ERR_NETWORK'
    
    const getErrorContent = () => {
      if (isAccessDenied) {
        return {
          icon: "🔒",
          title: "Access Denied",
          description: "You don't have permission to view the analytics dashboard.",
          details: "To access analytics, you need the 'analytics:view' permission. Please contact your administrator to request access.",
          showRetry: false,
          cardClass: "border-orange-200 dark:border-orange-800"
        }
      }
      
      if (isNotFound) {
        return {
          icon: "🔍",
          title: "Analytics Not Found",
          description: "The analytics service could not be found.",
          details: "Please ensure the analytics service is running and properly configured.",
          showRetry: true,
          cardClass: ""
        }
      }
      
      if (isServerError) {
        return {
          icon: "⚠️",
          title: "Server Error",
          description: "The analytics service encountered an error.",
          details: "Please try again later or contact support if the problem persists.",
          showRetry: true,
          cardClass: ""
        }
      }
      
      if (isNetworkError) {
        return {
          icon: "🌐",
          title: "Network Error",
          description: "Unable to connect to the analytics service.",
          details: "Please check your internet connection and try again.",
          showRetry: true,
          cardClass: ""
        }
      }
      
      return {
        icon: "❌",
        title: "Analytics Unavailable",
        description: errorObj?.message || "We're unable to load the analytics dashboard right now.",
        details: "This might be due to a network issue or server problem. Please try again or contact support.",
        showRetry: true,
        cardClass: ""
      }
    }
    
    const errorContent = getErrorContent()
    
    return (
      <div className="container mx-auto p-6">
        <Card className={errorContent.cardClass}>
          <CardContent className="p-12">
            <div className="flex flex-col items-center gap-6 text-center">
              <span className="text-5xl">{errorContent.icon}</span>
              <div className="space-y-3 max-w-md">
                <h1 className="text-2xl font-bold">{errorContent.title}</h1>
                <p className="text-muted-foreground">
                  {errorContent.description}
                </p>
                <p className="text-sm text-muted-foreground">
                  {errorContent.details}
                </p>
              </div>
              <div className="flex gap-3 mt-4">
                {errorContent.showRetry && (
                  <Button onClick={() => reset()} className="gap-2">
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={() => window.history.back()}
                >
                  Go Back
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  },

})